import React, { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { IntroVideo } from "./components/IntroVideo";

// Context
import { CartProvider } from "./context/CartContext";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import { WishlistProvider } from "./context/WishlistContext";

// Layout Components
import { Navbar, Footer } from "./components/Layout";
import { WhatsAppPopup } from "./components/WhatsAppPopup";

// Pages
import { HomePage } from "./pages/HomePage";
import { ShopPage } from "./pages/ShopPage";
import { ProductDetailPage } from "./pages/ProductDetailPage";
import { CartPage, CheckoutSuccessPage } from "./pages/CartPage";
import { ContactPage } from "./pages/ContactPage";
import { AboutPage } from "./pages/AboutPage";
import { FAQPage } from "./pages/FAQPage";
import { PoliciesPage } from "./pages/PoliciesPage";
import { AdminLoginPage } from "./pages/AdminLoginPage";
import { OrderTrackingPage } from "./pages/OrderTrackingPage";
import { WishlistPage } from "./pages/WishlistPage";
import { BlogPage, BlogPostPage } from "./pages/BlogPage";
import {
  AdminLayout,
  AdminDashboard,
  AdminProducts,
  AdminOrders,
  AdminAnalytics,
  AdminSettings,
  AdminAbout,
  AdminFAQ,
  AdminCategories,
  AdminReports,
  AdminBlog,
  AdminReviews,
  AdminPolicies,
} from "./pages/AdminPages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Analytics Tracker Component
const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    let sessionId = sessionStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem('session_id', sessionId);
    }

    const trackPageView = async () => {
      try {
        // Get IP-based location
        let locationData = null;
        try {
          const geoRes = await fetch('https://ipapi.co/json/');
          if (geoRes.ok) {
            const geoData = await geoRes.json();
            locationData = `${geoData.city}, ${geoData.region}, ${geoData.country_name}`;
          }
        } catch (e) {
          console.log('Could not get location');
        }

        await axios.post(`${API}/analytics`, {
          session_id: sessionId,
          event_type: 'page_view',
          page: location.pathname,
          user_agent: navigator.userAgent,
          location: locationData,
        });
      } catch (error) {
        console.error('Analytics error:', error);
      }
    };

    trackPageView();

    const startTime = Date.now();
    const trackDuration = () => {
      const duration = Math.floor((Date.now() - startTime) / 1000);
      if (duration > 5) {
        axios.post(`${API}/analytics`, {
          session_id: sessionId,
          event_type: 'duration',
          page: location.pathname,
          duration_seconds: duration,
        }).catch(() => {});
      }
    };

    window.addEventListener('beforeunload', trackDuration);
    return () => {
      trackDuration();
      window.removeEventListener('beforeunload', trackDuration);
    };
  }, [location.pathname]);

  return null;
};

// Public Layout
const PublicLayout = ({ children }) => {
  const [settings, setSettings] = React.useState(null);

  React.useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
      <WhatsAppPopup phoneNumber={settings?.whatsapp_number || '+1234567890'} />
    </>
  );
};

function App() {
  const [showIntro, setShowIntro] = useState(() => {
    return !sessionStorage.getItem('intro_seen');
  });

  const handleIntroComplete = useCallback(() => {
    sessionStorage.setItem('intro_seen', 'true');
    setShowIntro(false);
  }, []);

  return (
    <CartProvider>
      <WishlistProvider>
        <AdminAuthProvider>
          <div className="App min-h-screen bg-cream">
            {showIntro && <IntroVideo onComplete={handleIntroComplete} />}
            <BrowserRouter>
              <AnalyticsTracker />
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
                <Route path="/shop" element={<PublicLayout><ShopPage /></PublicLayout>} />
                <Route path="/product/:id" element={<PublicLayout><ProductDetailPage /></PublicLayout>} />
                <Route path="/cart" element={<PublicLayout><CartPage /></PublicLayout>} />
                <Route path="/checkout/success" element={<PublicLayout><CheckoutSuccessPage /></PublicLayout>} />
                <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
                <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
                <Route path="/faq" element={<PublicLayout><FAQPage /></PublicLayout>} />
                <Route path="/policies/:type" element={<PublicLayout><PoliciesPage /></PublicLayout>} />
                <Route path="/track-order" element={<PublicLayout><OrderTrackingPage /></PublicLayout>} />
                <Route path="/wishlist" element={<PublicLayout><WishlistPage /></PublicLayout>} />
                <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} />
                <Route path="/blog/:slug" element={<PublicLayout><BlogPostPage /></PublicLayout>} />

                {/* Admin Login */}
                <Route path="/admin/login" element={<AdminLoginPage />} />

                {/* Admin Routes */}
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="reviews" element={<AdminReviews />} />
                  <Route path="policies" element={<AdminPolicies />} />
                  <Route path="analytics" element={<AdminAnalytics />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="about" element={<AdminAbout />} />
                  <Route path="faq" element={<AdminFAQ />} />
                  <Route path="categories" element={<AdminCategories />} />
                  <Route path="reports" element={<AdminReports />} />
                  <Route path="blog" element={<AdminBlog />} />
                </Route>
              </Routes>
              <Toaster position="bottom-right" richColors />
            </BrowserRouter>
          </div>
        </AdminAuthProvider>
      </WishlistProvider>
    </CartProvider>
  );
}

export default App;
